// apps/web/src/features/profile/components/ProfileMenu.tsx
//
// Top-right profile plate + dropdown. The dropdown surfaces three navigation
// items routed through the i18n `shell.profile.*` keys: profile, settings,
// logout. Change-password lives on the Profile page now; this component
// does not own a modal.
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLogout } from '@magimp/shared-hooks';
import { Avatar, Menu, MenuItem } from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { clearWithBroadcast } from '../../../api/authEvents.js';

export function ProfileMenu() {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const logout = useLogout();
  const user = useAuthStore((s) => s.user);

  const onLogout = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        // Clear locally and propagate to sister tabs in the same profile
        // so a logout in one tab logs the user out everywhere.
        clearWithBroadcast('LOGOUT');
        navigate('/login');
      },
    });
  };

  if (!user) return null;
  const roleLabel = user.role.displayName;

  return (
    <Menu
      minWidth={200}
      trigger={(triggerProps) => (
        <button
          ref={triggerProps.ref as (el: HTMLButtonElement | null) => void}
          type="button"
          aria-label={t('profile.menu')}
          aria-haspopup={triggerProps['aria-haspopup']}
          aria-expanded={triggerProps['aria-expanded']}
          aria-controls={triggerProps['aria-controls']}
          onClick={triggerProps.onClick}
          onKeyDown={triggerProps.onKeyDown}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 8px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--fg-1)',
          }}
        >
          <Avatar name={user.name} />
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              lineHeight: 1.2,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 500 }}>{user.name}</span>
            <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{roleLabel}</span>
          </span>
        </button>
      )}
    >
      <div
        style={{
          padding: '8px 10px',
          borderBottom: '1px solid var(--border-2)',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>{user.name}</div>
        <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{roleLabel}</div>
      </div>
      <MenuItem onClick={() => navigate('/profile')}>{t('profile.profile')}</MenuItem>
      <MenuItem onClick={() => navigate('/settings')}>{t('profile.settings')}</MenuItem>
      <MenuItem onClick={onLogout}>{t('profile.logout')}</MenuItem>
    </Menu>
  );
}
